import { AuthError } from '../../shared/error';
import { ErrorResponse, isAPIErrorResponse } from './errorHandling';
import { progressPromise } from './progressPromise';

export type Method = 'delete' | 'get' | 'head' | 'patch' | 'post' | 'put';

export class RequestError extends Error {
  public override name = 'RequestError';
  public constructor(
    public readonly reason: string,
    public readonly requestOptions: RequestOptions<unknown>,
    public readonly response: Response<unknown>,
  ) {
    super(`Request failed ${reason}`);
  }
}

export class APIError extends RequestError {
  public override readonly name = 'APIError';
  public constructor(
    public readonly apiResponse: ErrorResponse,
    requestOptions: RequestOptions<unknown>,
    response: Response<unknown>,
  ) {
    super('API Error', requestOptions, response);
  }
}

export type QueryParameters = Record<string, boolean | number | string>;

interface ReadRequestOptions {
  url: string;
  query?: QueryParameters;
  headers?: HeadersInit;
  retry?: boolean;
}

export interface RequestOptions<T> extends ReadRequestOptions {
  method?: Method;
  payload?: T;
}

interface ListRequestOptions extends ReadRequestOptions {
  page?: number;
}

interface Response<T> {
  payload: T;
  headers: Headers;
  status: number;
}

export interface ListResponse<T> {
  items: T[];
  totalCount: number;
  hasMore: boolean;
}

interface FileRequestOptions
  extends RequestOptions<
    | ArrayBuffer
    | ArrayBufferView
    | Blob
    | Document
    | FormData
    | ReadableStream<Uint8Array>
    | URLSearchParams
    | string
  > {
  progressor: (progress: number) => void;
}

const isoDateMatch = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/;

const jsonReviver = (_key: string, value: unknown) => {
  if (typeof value === 'string' && isoDateMatch.test(value)) {
    return new Date(value);
  }

  return value;
};

function makeQueryString(query: QueryParameters): string {
  const segments: string[] = [];
  for (const [key, value] of Object.entries(query)) {
    segments.push(`${encodeURIComponent(key)}=${encodeURIComponent(value.toString())}`);
  }

  return `?${segments.join('&')}`;
}

export class APIClient {
  public async sendFile<T = void>(opts: FileRequestOptions): Promise<T> {
    let { url } = opts;
    const { method = 'POST', progressor, payload, retry, query } = opts;
    return (await progressPromise<T | undefined, number>((resolve, reject, progress) => {
      const methodUpper = method.toUpperCase();
      const xhr = new XMLHttpRequest();
      xhr.responseType = 'json';
      xhr.withCredentials = true;
      // eslint-disable-next-line unicorn/prefer-add-event-listener
      xhr.onerror = () => {
        reject(new RequestError('Failed to upload file', opts, this.xhrResponseObject(xhr)));
      };

      xhr.addEventListener('abort', () => {
        reject(new RequestError('Request aborted', opts, this.xhrResponseObject(xhr)));
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 204) {
          resolve(undefined);
          return;
        }

        if (xhr.status >= 200 && xhr.status <= 299) {
          resolve(xhr.response as T);
          return;
        }

        if (isAPIErrorResponse(xhr.response)) {
          if (this.shouldCSRFRetry(xhr.status, xhr.response) && retry !== false) {
            resolve(
              this.sendFile<T>({
                ...opts,
                retry: false,
              }),
            );

            return;
          }

          reject(new APIError(xhr.response, opts, this.xhrResponseObject(xhr)));
        }

        reject(new RequestError('Failed to upload', opts, this.xhrResponseObject(xhr)));
      });

      xhr.addEventListener('progress', (ev) => {
        progress(ev.loaded / ev.total);
      });

      if (query) {
        url += makeQueryString(query);
      }

      xhr.open(methodUpper, url);
      const csrf = this.getCSRF();
      if (csrf) {
        xhr.setRequestHeader('x-csrf', csrf);
      }

      xhr.send(payload as XMLHttpRequestBodyInit);
    }, progressor)) as T;
  }

  public async listRequest<T extends object>(
    opts: ListRequestOptions | string,
    page = 0,
  ): Promise<ListResponse<T>> {
    if (typeof opts === 'object') {
      opts.page = page;
    } else {
      opts = {
        page,
        url: opts,
      };
    }

    const res = await this.requestInternal<T[]>(opts);
    const totalCount = Number.parseInt(res.headers.get('x-total-count') || '0', 10);
    const pageSize = Number.parseInt(res.headers.get('x-page-size') || '0', 10);
    const hasMore = totalCount > (page + 1) * pageSize;
    return {
      hasMore,
      items: res.payload,
      totalCount,
    };
  }

  public async request<T = void>(opts: RequestOptions<object | undefined> | string): Promise<T> {
    return (await this.requestInternal<T>(opts)).payload;
  }

  private async requestInternal<T = void>(
    opts: RequestOptions<object | undefined> | string,
  ): Promise<Response<T>> {
    opts = typeof opts === 'object' ? opts : { url: opts };
    let { headers = {}, url } = opts;
    const { payload, query, method = 'get', retry } = opts;
    headers = new Headers(headers);
    if (payload) {
      headers.append('Content-Type', 'application/json; charset=utf-8');
    }

    const csrf = this.getCSRF();
    if (csrf) {
      headers.append('x-csrf', csrf);
    }

    if (query) {
      url += makeQueryString(query);
    }

    const response = await fetch(url, {
      body: payload ? JSON.stringify(payload) : undefined,
      credentials: 'same-origin',
      headers,
      method: method.toUpperCase(),
    });

    const { status, headers: responseHeader } = response;
    const body = await response.text();
    try {
      let json: unknown;
      if (body !== '') {
        json = JSON.parse(body, jsonReviver) as unknown;
      }

      if (status >= 200 && status <= 299) {
        return {
          headers: response.headers,
          payload: json as T,
          status: response.status,
        };
      }

      if (isAPIErrorResponse(json)) {
        if (this.shouldCSRFRetry(status, json) && retry !== false) {
          return await this.requestInternal<T>({
            ...opts,
            retry: false,
          });
        }

        throw new APIError(json, opts, {
          headers: responseHeader,
          payload,
          status,
        });
      }

      throw new RequestError('Unexpected response format', opts, {
        headers: responseHeader,
        payload: json,
        status,
      });
    } catch (error) {
      if (error instanceof SyntaxError) {
        throw new RequestError('Expected JSON', opts, {
          headers: responseHeader,
          payload: body,
          status,
        });
      }

      throw error;
    }
  }

  private getCSRF(): string | undefined {
    const split = document.cookie.split(';');
    const csrf = split.find((s) => s.includes('csrf'));
    if (!csrf) {
      return;
    }

    const match = /csrf=(.+)$/.exec(csrf);
    if (match) {
      return match[1];
    }

    return undefined;
  }

  private shouldCSRFRetry(status: number, json: ErrorResponse): boolean {
    const code = json.errors.authentication?.code;
    return (
      status === 401 &&
      (code === AuthError.CSRFMismatch ||
        code === AuthError.CSRFMissingCookie ||
        code === AuthError.CSRFMissingHeader)
    );
  }

  private xhrResponseObject(xhr: XMLHttpRequest): Response<unknown> {
    return {
      headers: new Headers(
        (xhr.getAllResponseHeaders() || '')
          .trim()
          .split(/[\n\r]+/)
          .map((r) => {
            const [key, rawValue] = r.split(':') as [string, string];
            return [key, rawValue.trim()] as [string, string];
          }),
      ),
      payload: xhr.response,
      status: xhr.status,
    };
  }
}
